import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  FormLabel,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

import { CreateTalentJob } from "../../../../../types/createJobSchema";
import CreateJobStepper from "../../../../../components/CreateJobStepper";

import PlayerInfo from "./PlayerInfo";
import SubmissionInfo from "./SubmissionInfo";
import JobDescription from "./JobDescription";
import CandidatesLocations from "./CandidatesLocations";

const PreviewAndPublishJobStep = ({ jobDetails }: { jobDetails: CreateTalentJob }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage.previewAndPublishJob" });
  const { control, watch } = useFormContext<CreateTalentJob>();

  const jobLocations = jobDetails.jobLocation;
  const isExternal = watch("isExternal");

  return (
    <Box sx={styles.container}>
      <Box sx={styles.stepContainer}>
        <CreateJobStepper currentStep="review-and-publish" />
        <Box sx={styles.content} component="main">
          <Typography sx={styles.title}>{t("previewAndPublishJob")}</Typography>

          <Box sx={styles.applicationMethodContainer}>
            <Typography sx={styles.sectionLabel}>{t("applicationMethod")}</Typography>
            <Controller
              name="isExternal"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset">
                  <RadioGroup
                    {...field}
                    value={field.value ? "external" : "goalngo"}
                    onChange={(e) => field.onChange(e.target.value === "external")}
                  >
                    <FormControlLabel
                      value="goalngo"
                      control={<Radio />}
                      label={t("throughGoalngo")}
                      sx={styles.radioLabel}
                    />
                    <FormControlLabel
                      value="external"
                      control={<Radio />}
                      label={t("throughExternalLink")}
                      sx={styles.radioLabel}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
            {isExternal && (
              <Controller
                name="externalLink"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth component="fieldset" error={!!error}>
                    <FormLabel error={!!error}>{t("externalLink")}</FormLabel>
                    <TextField
                      {...field}
                      fullWidth
                      placeholder={t("enterExternalLink")}
                      error={!!error}
                      helperText={error?.message ? t(error.message) : ""}
                    />
                  </FormControl>
                )}
              />
            )}
          </Box>

          <Box sx={styles.previewContainer}>
            <PlayerInfo jobDetails={jobDetails} />
            <Divider flexItem orientation="horizontal" />
            <JobDescription description={jobDetails.jobDescription} />
            <Divider flexItem orientation="horizontal" />
            <CandidatesLocations countries={jobLocations} />
            <Divider flexItem orientation="horizontal" />
            <SubmissionInfo jobDetails={jobDetails} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PreviewAndPublishJobStep;

const styles = {
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  stepContainer: {
    display: "flex",
    gap: "24px",
    maxWidth: "1140px",
    width: "80%",
    margin: "0 auto",
    flex: 1,
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "20px 24px",
  },
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  applicationMethodContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "#FFF",
  },
  sectionLabel: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  radioLabel: {
    "& .MuiFormControlLabel-label": {
      fontFamily: "Montserrat",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "20px",
    },
  },
  externalLinkInput: {
    marginTop: "8px",
  },
  previewContainer: {
    display: "flex",
    padding: "32px",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Gray---25, #F7F8F8)",
  },
};
